import { z } from 'zod';

/**
 * Contrat d'un tenant Alterrego.
 * Une app = un tenant.config.json validé par ce schéma.
 */

export const ModuleIdSchema = z.enum([
  'mysafe',
  'payment',
  'reservation',
  'cave',
  'vcard',
  'loyalty',
  'pos',
  'maia',
  'scanner',
  'salle',
  'carte',
  'agenda',
  'carte-cadeau',
  'parrainage',
  'tap-to-pay',
  'apple-auth',
]);
export type ModuleId = z.infer<typeof ModuleIdSchema>;

export const StageIdSchema = z.enum(['mockup', 'webapp', 'dev', 'beta', 'prod']);
export type StageId = z.infer<typeof StageIdSchema>;

export const DataSourceSchema = z.enum(['mock', 'live']);
export const EnvSchema = z.enum(['mock', 'test', 'live']);
export const AuthProviderSchema = z.enum(['apple', 'mysafe', 'disabled']);
export type AuthProvider = z.infer<typeof AuthProviderSchema>;

export const HostingSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('alterrego'),
    subdomain: z.string().regex(/^[a-z][a-z0-9-]*$/),
  }),
  z.object({
    mode: z.literal('custom'),
    domain: z.string().regex(/^([a-z0-9-]+\.)+[a-z]{2,}$/i),
  }),
  z.object({
    mode: z.literal('none'),
  }),
]);
export type Hosting = z.infer<typeof HostingSchema>;

export const StageConfigSchema = z.object({
  dataSource: DataSourceSchema,
  env: EnvSchema,
  auth: z.array(AuthProviderSchema).min(1),
  native: z.boolean().default(false),
  hosting: HostingSchema,
});
export type StageConfig = z.infer<typeof StageConfigSchema>;

export const BrandingSchema = z.object({
  primary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#1464B4'),
  secondary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#2864A0'),
  dark: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#3C3C50'),
  logo: z.string().url().optional(),
  splash: z.string().url().optional(),
  fontFamily: z.string().default('DM Sans'),
});
export type Branding = z.infer<typeof BrandingSchema>;

export const ALTERREGO_DEFAULTS = {
  apple: {
    teamId: 'QEXEC8JTET',
    redirectUri: 'https://api.apps.alterrego.com/auth/apple/callback',
  },
  backend: {
    provider: 'supabase' as const,
    url: 'https://api.apps.alterrego.com',
  },
} as const;

export const AuthConfigSchema = z.object({
  apple: z
    .object({
      serviceId: z.string().min(1),
      redirectUri: z.string().url().default(ALTERREGO_DEFAULTS.apple.redirectUri),
      teamId: z.string().regex(/^[A-Z0-9]{10}$/).default(ALTERREGO_DEFAULTS.apple.teamId),
    })
    .optional(),
  mysafe: z
    .object({
      issuer: z.string().url().default('https://id.alterrego.com'),
      clientId: z.string().min(1),
    })
    .optional(),
});
export type AuthConfig = z.infer<typeof AuthConfigSchema>;

export const BackendProviderSchema = z.enum(['supabase', 'self-hosted']);

export const BackendSchema = z.object({
  provider: BackendProviderSchema.default('supabase'),
  url: z.string().url().default('https://api.apps.alterrego.com'),
  anonKey: z.string().optional(),
});
export type Backend = z.infer<typeof BackendSchema>;

export const TapToPaySchema = z.object({
  provider: z.enum(['adyen']).default('adyen'),
  environment: z.enum(['test', 'live']).default('test'),
  merchantAccount: z.string().min(1),
  storeId: z.string().min(1),
  terminalId: z.string().default('auto'),
}).optional();
export type TapToPayConfig = z.infer<typeof TapToPaySchema>;

export const ComplianceOverrideSchema = z.object({
  rule: z.string().min(1),
  justification: z.string().min(50),
  counselName: z.string().min(1),
  counselBar: z.string().min(1),
  validatedAt: z.string().datetime(),
});
export type ComplianceOverride = z.infer<typeof ComplianceOverrideSchema>;

export const LegalSchema = z.object({
  legalName: z.string().min(1),
  siret: z.string().regex(/^\d{14}$/),
  headquarters: z.string().min(1),
  dpoContact: z.string().email(),
  role: z.enum(['controller', 'joint-controller']).default('controller'),
});
export type Legal = z.infer<typeof LegalSchema>;

export const MerchantsDirectorySchema = z.object({
  /** Source de l'annuaire commerces : 'ciss' (réseau partenaire wspointdevente)
   * ou 'apidae' (annuaire touristique Savoie Mont-Blanc filtré). */
  source: z.enum(['ciss', 'apidae']).default('ciss'),
  /** Identifiant de groupe envoyé par le front au backend (VITE_<TENANT>_GROUP_ID).
   * Pour 'ciss' = id numérique du groupe ; pour 'apidae' = clé de config backend. */
  groupId: z.string().min(1),
  /** Filtre appliqué côté backend quand source='apidae'. */
  apidaeFilter: z.object({
    includeCategories: z.array(z.string()).optional(),
    excludeTypesDetailles: z.array(z.string()).optional(),
  }).optional(),
}).optional();
export type MerchantsDirectory = z.infer<typeof MerchantsDirectorySchema>;

export const WeatherSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  cityLabel: z.string().min(1),
}).optional();
export type Weather = z.infer<typeof WeatherSchema>;

const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

export const ThemeColorsSchema = z.object({
  primary: HexColor.optional(),
  secondary: HexColor.optional(),
  accent: HexColor.optional(),
  surface: HexColor.optional(),
  surfaceAlt: HexColor.optional(),
  text: HexColor.optional(),
  textMuted: HexColor.optional(),
  border: HexColor.optional(),
  success: HexColor.optional(),
  warning: HexColor.optional(),
  error: HexColor.optional(),
});
export type ThemeColors = z.infer<typeof ThemeColorsSchema>;

export const ThemeOverrideSchema = z.object({
  colors: ThemeColorsSchema.optional(),
  typography: z.object({ fontFamily: z.string().optional() }).optional(),
}).optional();
export type ThemeOverride = z.infer<typeof ThemeOverrideSchema>;

export const TenantConfigSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1),
  bundleId: z.string().regex(/^[a-z][a-z0-9.]*[a-z0-9]$/),
  ascAppId: z.string().optional(),
  merchantsDirectory: MerchantsDirectorySchema,
  weather: WeatherSchema,
  modules: z.array(ModuleIdSchema).min(1),
  branding: BrandingSchema,
  theme: ThemeOverrideSchema,
  stages: z.object({
    mockup: StageConfigSchema,
    webapp: StageConfigSchema,
    dev: StageConfigSchema,
    beta: StageConfigSchema,
    prod: StageConfigSchema,
  }),
  authConfig: AuthConfigSchema,
  backend: BackendSchema.default({}),
  tapToPay: TapToPaySchema,
  legal: LegalSchema,
  complianceOverrides: z.array(ComplianceOverrideSchema).default([]),
  locale: z.string().default('fr-FR'),
  features: z.record(z.boolean()).default({}),
});
export type TenantConfig = z.infer<typeof TenantConfigSchema>;

export function loadTenantConfig(raw: unknown): TenantConfig {
  const parsed = TenantConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `tenant.config.json invalide:\n${JSON.stringify(parsed.error.format(), null, 2)}`,
    );
  }
  return parsed.data;
}

export function hasModule(config: TenantConfig, id: ModuleId): boolean {
  return config.modules.includes(id);
}

export function getStage(config: TenantConfig, stage: StageId): StageConfig {
  return config.stages[stage];
}

export function resolveHostingUrl(stage: StageConfig): string | null {
  if (stage.hosting.mode === 'alterrego') {
    return `https://${stage.hosting.subdomain}.apps.alterrego.com`;
  }
  if (stage.hosting.mode === 'custom') {
    return `https://${stage.hosting.domain}`;
  }
  return null;
}

export { encodeAppleState, decodeAppleState, isAllowedReturnUrl } from './apple-state';
export type { AppleAuthState } from './apple-state';
